import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import FloatingAssistant from '../components/FloatingAssistant';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  McButton,
  McCard,
  McInput,
  McTextarea,
  McIcon,
  McTag,
  McSelect,
  McTable,
  McLoadingIndicator,
  McStepIndicator,
  McNotification,
  McDialog,
  McTabBar,
  McTab,
} from '@maersk-global/mds-react-wrapper';
import { McTooltip } from '@maersk-global/mds-react-wrapper/components-core/mc-tooltip';
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryService } from '../services/queryService';
import { agentService, PromptTemplateDTO } from '../services/agentService';
import { getUserRoles, hasElevatedAdminAccess } from '../services/authService';
import { useNotification } from '../context/NotificationContext';
import { sanitizeInput, validateQueryId, formatQueryId } from '../utils/validators';
import { DatabaseType } from '../utils/terminalConfig';
import { TerminalSelect } from '../components/Common/TerminalSelect';
import { DatabaseSelect } from '../components/Common/DatabaseSelect';
import { ConnectionSelect } from '../components/Common/ConnectionSelect';
import { extractParametersFromSql, getAppRoot } from '../utils/queryUtils';
import { SchemaExplorer } from '../components/SchemaExplorer';
import { useApplicationAccess } from '../hooks/useApplicationAccess';
import { QUERY_CATEGORIES, getCategoryOptions } from '../utils/categoryConfig';
import { ConfirmationDialog } from '../components/Common/ConfirmationDialog';
import { SqlEditor } from '../components/Common/SqlEditor';
import type { SqlEditorHandle } from '../components/Common/SqlEditor';
import { useSchemaPanelState } from '../hooks/useSchemaPanelState';
import { applyLeadingSpace } from '../utils/schemaInsertion';
import { SqlResultsDialog } from '../components/Common/SqlResultsDialog';
import { SqlDiffDialog } from '../components/Common/SqlDiffDialog';
import { DownloadResultsButton } from '../components/Common/DownloadResultsButton';
import { ResultFilterBar } from '../components/Common/ResultFilterBar';
import { ExplainPlanCard } from '../components/Common/ExplainPlanCard';
import { ChartVisualization } from '../components/Common/ChartVisualization';
import { QueryListSidebar } from '../components/queries/QueryListSidebar';
import { ResultsVisualization } from '../components/Common/ResultsVisualization';
import ParameterRow from '../components/Common/ParameterRow';
import { classifySqlError } from '../utils/sqlErrorClassifier';
import { roleConfig } from '../config/roles';
// Removed MUI Switch as it's no longer used for AI Assist toggle

interface Parameter {
  name: string;
  type: string;
  required: boolean;
  defaultValue: string;
  description: string;
}

export interface ApplicationOption {
  value: string;
  label: string;
}

// EXPERT_PROMPT_CATEGORIES moved to ../constants/prompts.ts

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  agentName?: string;
}

export const resolveAgentErrorMessage = (error: any): string => {
  const data = error?.response?.data;

  // Handle 429 quota errors first — format the reset time in the user's locale
  if (error?.response?.status === 429) {
    const resetMs = data?.windowResetEpochMs;
    const resetInfo = resetMs
      ? ` You can try again after ${new Date(resetMs).toLocaleString()}.`
      : '';
    return `You have reached your AI usage limit for this quota period.${resetInfo}`;
  }

  const message =
    data?.message ||
    data?.detail ||
    data?.validationResult?.message ||
    data?.clarificationMessage;

  if (message && String(message).trim()) {
    return String(message).trim();
  }

  const sqlQuery = data?.sqlQuery;
  if (typeof sqlQuery === 'string') {
    const extracted = sqlQuery
      .split('\n')
      .map((line: string) => line.replace(/^\s*--\s?/, '').trim())
      .filter(Boolean)
      .find((line: string) => !line.toLowerCase().includes('error generating query'));
    if (extracted) return extracted;
  }

  return error?.message || 'Unknown error';
};

const extractSqlFromText = (text: string): string => {
  const raw = String(text || '').trim();
  if (!raw) return '';

  // Prefer fenced SQL/code blocks when present.
  const fencedSql = raw.match(/```sql\s*([\s\S]*?)```/i);
  if (fencedSql?.[1]?.trim()) return fencedSql[1].trim();

  const fencedAny = raw.match(/```\s*([\s\S]*?)```/i);
  if (fencedAny?.[1]?.trim()) return fencedAny[1].trim();

  const lowered = raw.toLowerCase();
  if (lowered.includes('error generating query')) {
    return '';
  }

  // If whole payload already looks like a SQL statement (starts with keyword
  // followed by whitespace and column/table patterns), use it directly.
  if (/^(WITH\s+\w|SELECT\s+[\w*"(]|INSERT\s+INTO|UPDATE\s+\w|DELETE\s+FROM|MERGE\s+INTO|CREATE\s+|ALTER\s+|DROP\s+)/i.test(raw)) {
    return raw;
  }

  // Do NOT try to find SQL keywords embedded in prose — that produces false
  // positives like extracting "select, as well as…" from clarification text.
  return '';
};

const extractSqlFromAgentResponse = (response: any): string => {
  // Only look in fields that actually carry SQL — never in clarificationMessage
  // or explanation which contain natural-language prose.
  const candidates = [
    response?.sqlQuery,
    response?.generatedSql,
  ];

  for (const candidate of candidates) {
    if (typeof candidate !== 'string') continue;
    const sql = extractSqlFromText(candidate);
    if (sql) return sql;
  }

  return '';
};

export const DEFAULT_AI_MODEL_OPTIONS = [
  { label: 'GPT-4o Mini', value: 'gpt-4o-mini' },
  { label: 'GPT-5.1 Codex Mini', value: 'gpt-5.1-codex-mini' },
];

export const resolveAiModelOptions = () => {
  const configured = process.env.REACT_APP_QUERY_BUILDER_MODELS;
  if (!configured) return DEFAULT_AI_MODEL_OPTIONS;

  const values = configured
    .split(',')
    .map((item) => item.trim())
    .filter(Boolean);

  if (values.length === 0) return DEFAULT_AI_MODEL_OPTIONS;

  return values.map((value) => ({ label: value, value }));
};

const hasAnyRole = (userRoles: string[], requiredRoles: string[]): boolean => {
  const normalizedUserRoles = new Set((userRoles || []).map((role) => String(role || '').toLowerCase()));
  return (requiredRoles || []).some((requiredRole) => normalizedUserRoles.has(String(requiredRole || '').toLowerCase()));
};

const isDdlStatement = (sql: string): boolean => {
  const normalized = String(sql || '').trim().toUpperCase();
  return /^(CREATE|ALTER|DROP|TRUNCATE|RENAME|COMMENT|GRANT|REVOKE)\b/.test(normalized);
};

export default function CreateQueryPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { showNotification } = useNotification();

  // Workflow state
  const [activeStep, setActiveStep] = useState(0);
  const steps = ['Draft Query', 'Finalize'];

  // Form state
  const [formData, setFormData] = useState({
    queryId: '',
    name: '',
    description: '',
    sqlContent: '',
    category: '',
    application: '', // Added application
    tags: [] as string[],
    changeNotes: '',
    createdBy: 'current-user',
    makeCurrent: true,
  });
  const formDataRef = useRef(formData);

  // Parameters state
  const [parameters, setParameters] = useState<Parameter[]>([]);
  const parametersRef = useRef(parameters);

  // Terminal & database state (for SQL editor autocomplete)
  const [selectedTerminal, setSelectedTerminal] = useState('');
  const selectedTerminalRef = useRef(selectedTerminal);
  const [databaseType, setDatabaseType] = useState<DatabaseType>('ORACLE');
  const databaseTypeRef = useRef(databaseType);
  const [connectionId, setConnectionId] = useState<string>('');
  const connectionIdRef = useRef(connectionId);
  const [externalDdlEnabled, setExternalDdlEnabled] = useState(false);
  const [externalConnectionServerType, setExternalConnectionServerType] = useState<'POSTGRESQL' | 'ORACLE' | undefined>(undefined);
  const [validationResult, setValidationResult] = useState<any>(null);
  const [testResults, setTestResults] = useState<any>(null);
  const [resultColumnFilters, setResultColumnFilters] = useState<Record<string, string>>({});
  const [testExecutionState, setTestExecutionState] = useState<'idle' | 'validating' | 'running' | 'success' | 'error'>('idle');
  const [isAiPopulating, setIsAiPopulating] = useState(false);
  const [showSchemaExplorer, setShowSchemaExplorer] = useState(false);
  const sqlEditorRef = useRef<SqlEditorHandle>(null);
  const { open: schemaPanelOpen, setOpen: setSchemaPanelOpen, toggle: toggleSchemaPanel } = useSchemaPanelState('create');
  const [schemaPanelTab, setSchemaPanelTab] = React.useState<'schema' | 'field-search' | 'field-catalog'>('schema');
  const [schemaExplorerWarning, setSchemaExplorerWarning] = useState<string | null>(null);
  const schemaOverlayHostRef = useRef<HTMLDivElement>(null);
  const [schemaBackdropTop, setSchemaBackdropTop] = useState(0);
  const openSchemaPanelTab = React.useCallback((tab: 'schema' | 'field-search' | 'field-catalog') => {
    if (!selectedTerminal) {
      setSchemaExplorerWarning('Please select a terminal first to open Schema Explorer.');
      setSchemaPanelOpen(false);
      return;
    }
    setSchemaExplorerWarning(null);
    if (schemaPanelOpen && schemaPanelTab === tab) {
      setSchemaPanelOpen(false);
      return;
    }
    setSchemaPanelTab(tab);
    setSchemaPanelOpen(true);
  }, [selectedTerminal, schemaPanelOpen, schemaPanelTab, setSchemaPanelOpen]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [splitRatio, setSplitRatio] = useState(50); // percentage for left panel in Step 1 (unused in new layout, kept for future)
  const [editorRatio, setEditorRatio] = useState(0.65);
  const [activeResultTab, setActiveResultTab] = useState<'results' | 'explain' | 'parameters' | 'visualization'>('results');
  const appliedPrefillRef = useRef(false);
  const editorColumnRef = useRef<HTMLDivElement>(null);
  const [isExplainingPlan, setIsExplainingPlan] = useState(false);
  const [aiExplanation, setAiExplanation] = useState<string | null>(null);
  const [isGeneratingAiExplanation, setIsGeneratingAiExplanation] = useState(false);
  const [aiCorrectionDiff, setAiCorrectionDiff] = useState<{ original: string; corrected: string } | null>(null);
  const [showDiffDialog, setShowDiffDialog] = useState(false);

  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const querySidebarPush = 'clamp(220px, 24vw, 300px)';

  // Handle auto-expanding textarea
  const handleAutoExpand = (e: any) => {
    const target = e.target;
    target.style.height = 'auto';
    target.style.height = `${target.scrollHeight}px`;
  };

  // AI Generation state (for floating assistant)
  const [aiPanelOpen, setAiPanelOpen] = useState(false); // Only for toggle UI
  const [chatHistory, setChatHistory] = useState<ChatMessage[]>([]);
  const chatHistoryRef = useRef<ChatMessage[]>([]);
  const [aiIntent, setAiIntent] = useState('');
  const [aiModelName, setAiModelName] = useState(() => {
    const options = resolveAiModelOptions();
    return options[0]?.value ?? 'gpt-4o-mini';
  });
  const aiIntentRef = useRef(aiIntent);
  const [aiDomainOverride, setAiDomainOverride] = useState('AUTO');
  const aiDomainRef = useRef(aiDomainOverride);
  const aiModelRef = useRef(aiModelName);
  const aiModelOptions = React.useMemo(() => resolveAiModelOptions(), []);
  const [autoFixModel, setAutoFixModel] = useState(() => {
    const options = resolveAiModelOptions();
    return options[0]?.value ?? 'gpt-4o-mini';
  });
  const autoFixModelRef = useRef(autoFixModel);
  const chatScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => { chatHistoryRef.current = chatHistory; }, [chatHistory]);
  useEffect(() => { aiIntentRef.current = aiIntent; }, [aiIntent]);
  useEffect(() => { aiDomainRef.current = aiDomainOverride; }, [aiDomainOverride]);
  useEffect(() => { aiModelRef.current = aiModelName; }, [aiModelName]);
  useEffect(() => { autoFixModelRef.current = autoFixModel; }, [autoFixModel]);

  // Close schema overlay when clicking outside (dropdown behavior)
  useEffect(() => {
    if (activeStep !== 0 || !schemaPanelOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (schemaOverlayHostRef.current && !schemaOverlayHostRef.current.contains(e.target as Node)) {
        setSchemaPanelOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeStep, schemaPanelOpen, setSchemaPanelOpen]);

  useEffect(() => {
    if (activeStep !== 0 || !schemaPanelOpen) return;
    const onEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSchemaPanelOpen(false);
    };
    window.addEventListener('keydown', onEsc);
    return () => window.removeEventListener('keydown', onEsc);
  }, [activeStep, schemaPanelOpen, setSchemaPanelOpen]);

  useEffect(() => {
    if (activeStep !== 0 || !schemaPanelOpen) return;
    const updateBackdropTop = () => {
      const hostBottom = schemaOverlayHostRef.current?.getBoundingClientRect().bottom ?? 0;
      setSchemaBackdropTop(hostBottom);
    };
    updateBackdropTop();
    window.addEventListener('resize', updateBackdropTop);
    window.addEventListener('scroll', updateBackdropTop, true);
    return () => {
      window.removeEventListener('resize', updateBackdropTop);
      window.removeEventListener('scroll', updateBackdropTop, true);
    };
  }, [activeStep, schemaPanelOpen]);

  useEffect(() => {
    if (activeStep !== 0 || !schemaPanelOpen) return;
    if (!selectedTerminal) setSchemaPanelOpen(false);
  }, [activeStep, schemaPanelOpen, selectedTerminal, setSchemaPanelOpen]);

  // Fetch AI agent templates for the dropdown
  const { data: aiTemplates } = useQuery<PromptTemplateDTO[]>({
    queryKey: ['promptTemplates', databaseType],
    queryFn: () => agentService.getPromptTemplates(databaseType),
    staleTime: 1000 * 60 * 5,
  });

  // State for selected query from sidebar
  const [selectedQueryId, setSelectedQueryId] = useState<string | null>(null);

  // Query to load selected query details
  const { data: selectedQuery } = useQuery({
    queryKey: ['query', selectedQueryId],
    queryFn: () => selectedQueryId ? queryService.getQuery(selectedQueryId) : null,
    enabled: !!selectedQueryId,
  });

  // Effect to populate form when query is selected
  useEffect(() => {
    if (selectedQuery) {
      const nextFormData = {
        queryId: selectedQuery.queryId || '',
        name: selectedQuery.name || '',
        description: selectedQuery.description || '',
        sqlContent: selectedQuery.sqlContent || '',
        category: selectedQuery.category || '',
        application: selectedQuery.applicationId || '',
        tags: selectedQuery.tags || [],
        changeNotes: '',
        createdBy: selectedQuery.createdBy || 'current-user',
        makeCurrent: true,
      };
      formDataRef.current = nextFormData;
      setFormData(nextFormData);

      const nextParameters = (selectedQuery.parameters || []).map(param => ({
        ...param,
        defaultValue: param.defaultValue || '',
        description: param.description || ''
      }));
      parametersRef.current = nextParameters;
      setParameters(nextParameters);
      // Reset test results when loading a new query
      setTestResults(null);
      setValidationResult(null);
      setTestExecutionState('idle');
      setActiveResultTab('results');
    }
  }, [selectedQuery]);

  // Optional prefill when navigated from Ref Data SQL preview.
  useEffect(() => {
    if (appliedPrefillRef.current) return;
    appliedPrefillRef.current = true;

    const state = location.state as {
      prefillSql?: string;
      prefillTerminal?: string;
      prefillAppId?: string;
    } | null;

    const sql = typeof state?.prefillSql === 'string' ? state.prefillSql.trim() : '';
    if (!sql) return;

    const nextFormData = {
      ...formDataRef.current,
      sqlContent: sql,
      application: state?.prefillAppId || formDataRef.current.application,
    };
    formDataRef.current = nextFormData;
    setFormData((prev) => ({
      ...prev,
      sqlContent: sql,
      application: state?.prefillAppId || prev.application,
    }));

    if (state?.prefillTerminal) {
      setSelectedTerminal(state.prefillTerminal);
    }

    showNotification('SQL prefilled from Ref Data.', 'success');
  }, [location.state, showNotification]);

  const aiDomainOptions = React.useMemo(() => {
    const opts = [{ label: 'Auto-Select (Supervisor)', value: 'AUTO' }];
    if (aiTemplates && aiTemplates.length > 0) {
      aiTemplates.forEach(t => opts.push({ label: `Expert Agent: ${t.name}`, value: t.templateId! }));
    }
    return opts;
  }, [aiTemplates]);

  useEffect(() => {
    if (!aiDomainOptions.length) return;
    const current = aiDomainRef.current;
    const hasCurrent = aiDomainOptions.some((opt) => String(opt.value) === String(current || ''));
    if (!hasCurrent) {
      const next = String(aiDomainOptions[0].value);
      setAiDomainOverride(next);
    }
  }, [aiDomainOptions]);

  useEffect(() => {
    if (!aiModelOptions.length) return;
    const current = aiModelRef.current;
    const hasCurrent = aiModelOptions.some((opt) => String(opt.value) === String(current || ''));
    if (!hasCurrent) {
      const next = String(aiModelOptions[0].value);
      setAiModelName(next);
    }
  }, [aiModelOptions]);



  // AI Generate mutation
  const aiGenerateMutation = useMutation({
    mutationFn: (data: any) => agentService.generateQuery(data),
    onSuccess: (response: any) => {
      const subAgentName: string | undefined = response.subAgentUsed || undefined;
      if (response.requiresClarification) {
        setChatHistory(prev => [...prev, { role: 'assistant', content: response.clarificationMessage, agentName: subAgentName }]);
        // Scroll logic is handled by effect
      } else {
        let generatedSql = response.sqlQuery ?? response.generatedSql ?? '';
        if (generatedSql.trim().endsWith(';')) {
          generatedSql = generatedSql.trim().slice(0, -1);
        }

        const explanation = response.explanation || 'Generated successfully.';
        setChatHistory(prev => [...prev, { role: 'assistant', content: `Here is the SQL:\n\n\`\`\`sql\n${generatedSql}\n\`\`\`\n\n${explanation}`, agentName: subAgentName }]);

        // Extract first user question from ref or intent ref if it's the first one
        const firstUserIntent = chatHistoryRef.current.find(m => m.role === 'user')?.content || aiIntentRef.current.trim() || 'Autogenerated Query';

        // Format the explanation to be valid multi-line SQL comments
        const formattedExplanation = explanation.split('\n').map((line: string) => `-- ${line}`).join('\n');

        const finalSql = `-- Goal: ${firstUserIntent.replace(/\n/g, ' ')}\n--\n${formattedExplanation}\n\n${generatedSql}`;
        handleFieldChange('sqlContent', finalSql);
        showNotification('SQL generated successfully! Review and edit below.', 'success');
      }
    },
    onError: (error: any) => {
      const resolvedMessage = resolveAgentErrorMessage(error);
      if (error?.response?.status !== 429) {
        showNotification(`AI generation failed: ${resolvedMessage}`, 'error');
      }
      setChatHistory(prev => [...prev, { role: 'assistant', content: `Error: ${resolvedMessage}` }]);
    },
  });

  const autoFixSqlMutation = useMutation({
    mutationFn: (data: any) => agentService.generateQuery(data),
    onSuccess: (response: any) => {
      // If the agent asks a clarification question, surface it instead of
      // silently failing with "did not return a fixed SQL query".
      if (response?.requiresClarification && response?.clarificationMessage) {
        showNotification(
          `AI Query Correction needs more info: ${response.clarificationMessage}`,
          'warning'
        );
        return;
      }

      let generatedSql = extractSqlFromAgentResponse(response);
      if (generatedSql.trim().endsWith(';')) {
        generatedSql = generatedSql.trim().slice(0, -1);
      }
      if (!generatedSql.trim()) {
        showNotification('AI Query Correction did not return a corrected SQL query.', 'warning');
        return;
      }

      const originalSql = formDataRef.current.sqlContent?.trim() || '';
      handleFieldChange('sqlContent', generatedSql);
      setAiCorrectionDiff({ original: originalSql, corrected: generatedSql });
      setValidationResult(null);
      setTestResults(null);
      setTestExecutionState('idle');
      showNotification('SQL corrected by AI. Review and execute again.', 'success');
    },
    onError: (error: any) => {
      const resolvedMessage = resolveAgentErrorMessage(error);
      showNotification(`Auto-correction failed: ${resolvedMessage}`, 'error');
    },
  });

  const handleAiGenerate = () => {
    const intent = aiIntentRef.current.trim();
    if (!intent) {
      showNotification('Please enter a message', 'warning');
      return;
    }

    setChatHistory(prev => [...prev, { role: 'user', content: intent }]);
    setAiIntent('');

    aiGenerateMutation.mutate({
      naturalLanguageQuery: intent,
      terminalId: selectedTerminalRef.current,
      databaseType: databaseTypeRef.current,
      pageContext: {
        sqlContent: formDataRef.current.sqlContent,
        queryName: formDataRef.current.name,
        description: formDataRef.current.description,
        category: formDataRef.current.category,
        tags: formDataRef.current.tags.join(', '),
      },
      options: {
        domainOverride: aiDomainRef.current,
        modelName: aiModelRef.current,
        useLiveSchema: false,
        detailedComments: true,
      },
      conversationHistory: chatHistory
    });
  };

  const handleAiRetryMessage = (message: string) => {
    const retryIntent = (message || '').trim();
    if (!retryIntent || aiGenerateMutation.isPending) {
      return;
    }

    setChatHistory(prev => [...prev, { role: 'user', content: retryIntent }]);
    setAiIntent('');

    aiGenerateMutation.mutate({
      naturalLanguageQuery: retryIntent,
      terminalId: selectedTerminalRef.current,
      databaseType: databaseTypeRef.current,
      pageContext: {
        sqlContent: formDataRef.current.sqlContent,
        queryName: formDataRef.current.name,
        description: formDataRef.current.description,
        category: formDataRef.current.category,
        tags: formDataRef.current.tags.join(', '),
      },
      options: {
        domainOverride: aiDomainRef.current,
        modelName: aiModelRef.current,
        useLiveSchema: false,
        detailedComments: true,
      },
      conversationHistory: chatHistoryRef.current,
    });
  };

  const buildSqlErrorContext = () => {
    const details: string[] = [];
    if (validationResult?.error) details.push(String(validationResult.error));
    if (validationResult?.validationMessage && !validationResult?.isValid) details.push(String(validationResult.validationMessage));
    if (Array.isArray(validationResult?.syntaxErrors)) details.push(...validationResult.syntaxErrors);
    if (Array.isArray(validationResult?.errors)) details.push(...validationResult.errors);
    if (Array.isArray(validationResult?.databaseValidationMessages)) details.push(...validationResult.databaseValidationMessages);
    if (Array.isArray(validationResult?.vulnerabilities)) {
      details.push(...validationResult.vulnerabilities.map((v: any) => String(v?.description || '')));
    }
    if (testResults?.errorMessage) details.push(String(testResults.errorMessage));
    if (testResults?.error) details.push(String(testResults.error));
    const merged = Array.from(new Set(details.map((d) => d?.trim()).filter(Boolean))).join('\n');
    return merged || 'SQL execution/validation failed.';
  };

  /**
   * Classifies the current error using the shared sqlErrorClassifier utility.
   */
  const classifyError = () => classifySqlError(buildSqlErrorContext());

  const handleResizerMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    const startY = e.clientY;
    const initialRatio = editorRatio;
    const onMove = (me: MouseEvent) => {
      const col = editorColumnRef.current;
      if (!col) return;
      const h = col.getBoundingClientRect().height;
      if (!h) return;
      setEditorRatio(Math.max(0.15, Math.min(0.85, initialRatio + (me.clientY - startY) / h)));
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [editorRatio]);

  const handleAutoFixSql = () => {
    const currentSql = formDataRef.current.sqlContent?.trim();
    if (!currentSql) {
      showNotification('SQL content is empty. Nothing to fix.', 'warning');
      return;
    }

    const classification = classifyError();
    if (!classification.fixable) {
      showNotification(classification.reason || 'This error cannot be fixed by rewriting SQL.', 'warning');
      return;
    }

    const errorContext = buildSqlErrorContext();
    autoFixSqlMutation.mutate({
      naturalLanguageQuery: [
        'Fix this SQL query based on the database validation/execution error.',
        'Return ONLY the corrected executable SQL inside a ```sql code block.',
        'Do NOT ask clarifying questions. Do NOT include explanations outside the code block.',
        'Do NOT add any comments in the SQL query.',
        `Error details:\n${errorContext}`,
        `SQL:\n${currentSql}`,
      ].join('\n\n'),
      terminalId: selectedTerminalRef.current || '',
      databaseType: databaseTypeRef.current,
      pageContext: {
        sqlContent: currentSql,
        validationError: errorContext,
      },
      options: {
        domainOverride: 'SQL_MIGRATION',
        modelName: autoFixModelRef.current,
        useLiveSchema: false,
        detailedComments: false,
      },
      conversationHistory: [],
    });
  };

  // Auto-scroll chat to bottom
  useEffect(() => {
    if (chatScrollRef.current) {
      chatScrollRef.current.scrollTop = chatScrollRef.current.scrollHeight;
    }
  }, [chatHistory]);

  // Keep refs in sync
  useEffect(() => { selectedTerminalRef.current = selectedTerminal; }, [selectedTerminal]);
  useEffect(() => { databaseTypeRef.current = databaseType; }, [databaseType]);
  useEffect(() => { connectionIdRef.current = connectionId; }, [connectionId]);

  const resultColumns = useMemo(() => {
    const normalizeResultColumns = (columns: any[]) => {
      return columns
        .map((column) => {
          if (typeof column === 'string') {
            return { id: column, label: column.toUpperCase() };
          }

          const id = String(column?.id ?? column?.label ?? '').trim();
          if (!id) {
            return null;
          }

          const label = String(column?.label ?? id.toUpperCase());
          return { id, label };
        })
        .filter(Boolean);
    };

    const providedColumns = Array.isArray(testResults?.columns) ? testResults.columns : [];
    if (providedColumns.length > 0) {
      return normalizeResultColumns(providedColumns);
    }

    const rows = Array.isArray(testResults?.data) ? testResults.data : [];
    if (rows.length === 0) {
      return [];
    }

    const seen = new Set<string>();
    const orderedKeys: string[] = [];

    rows.forEach((row: any) => {
      if (!row || typeof row !== 'object') {
        return;
      }

      Object.keys(row).forEach((key) => {
        if (!seen.has(key)) {
          seen.add(key);
          orderedKeys.push(key);
        }
      });
    });

    return orderedKeys.map((key) => ({ id: key, label: key.toUpperCase() }));
  }, [testResults]);

  const filteredTestResults = useMemo(() => {
    const rows = testResults?.data || [];
    const activeFilters = Object.entries(resultColumnFilters).filter(([, v]) => v && v.trim());
    if (activeFilters.length === 0) {
      return rows;
    }
    return rows.filter((row: any) =>
      activeFilters.every(([columnId, filterValue]) =>
        String(row?.[columnId] ?? '')
          .toLowerCase()
          .includes(filterValue.toLowerCase())
      )
    );
  }, [testResults, resultColumnFilters]);

  const activeResultFilterCount = useMemo(
    () => Object.values(resultColumnFilters).filter((v) => v && v.trim()).length,
    [resultColumnFilters]
  );

  useEffect(() => {
    setResultColumnFilters({});
  }, [testResults?.data]);

  // UI state
  const [currentTag, setCurrentTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});

  // Categories used from utils/categoryConfig


  // Parameter types
  const parameterTypes = ['STRING', 'NUMBER', 'BOOLEAN', 'DATE', 'TIMESTAMP', 'STRING_LIST'];

  // Auto-extract parameters when SQL changes

  // Applications loading
  const { data: allApplications = [] } = useQuery({
    queryKey: ['applications'],
    queryFn: () => queryService.getApplications(),
  });

  const [activeApplications, setActiveApplications] = useState<ApplicationOption[]>([]);

  useEffect(() => {
    const roles = getUserRoles();
    if (allApplications.length > 0) {
      const filtered = hasElevatedAdminAccess(roles)
        ? allApplications.map((app: any) => ({ value: app.applicationId, label: app.name || app.applicationId }))
        : allApplications
            .filter((app: any) => roles.some(role => {
              const r = (role || '').toLowerCase();
              const appId = (app.applicationId || '').toLowerCase();
              const appName = (app.name || '').toLowerCase();
              const appRoot = getAppRoot(appId);
              const roleRoot = getAppRoot(r);
              return roleRoot === appRoot || r === appName;
            }))
            .map((app: any) => ({ value: app.applicationId, label: app.name || app.applicationId }));
      setActiveApplications(filtered);
      // Auto-select if only one
      if (filtered.length === 1 && !formData.application) {
        formDataRef.current = { ...formDataRef.current, application: filtered[0].value };
        setFormData(prev => ({ ...prev, application: filtered[0].value }));
      }
    }
  }, [allApplications]);

  useEffect(() => {
    const extracted = extractParametersFromSql(formData.sqlContent);
    const extractedNames = new Set(extracted.map(p => p.name));

    const newParameters = (() => {
      const prev = parametersRef.current;
      // 1. Update existing parameters (keep their values) if they still exist in SQL
      const updatedExisting = prev.filter(p => extractedNames.has(p.name));

      // 2. Add new parameters that aren't in the list
      const newParams = extracted
        .filter(ep => !prev.find(p => p.name === ep.name))
        .map(ep => ({
          name: ep.name,
          type: 'STRING',
          required: true,
          defaultValue: '',
          description: ''
        }));

      return [...updatedExisting, ...newParams];
    })();
    parametersRef.current = newParameters;
    setParameters(newParameters);
    if (newParameters.length > 0) {
      setActiveResultTab('parameters');
    } else {
      setActiveResultTab(prev => prev === 'parameters' ? 'results' : prev);
    }
  }, [formData.sqlContent]);

  // Query Mutations
  const createMutation = useMutation({
    mutationFn: (data: any) => queryService.createQuery(data),
    onSuccess: (response) => {
      showNotification(`Query "${response.name}" created successfully!`, 'success');
      navigate(`/queries/${response.queryId}`);
    },
    onError: (error: any) => {
      showNotification(`Failed to create query: ${error.response?.data?.message || error.message}`, 'error');
    },
  });

  const validateMutation = useMutation({
    mutationFn: (data: any) => queryService.validateQuery(data),
    onSuccess: (response) => {
      setValidationResult({
        isValid: response.isValid,
        executionTime: response.executionTimeMs,
        vulnerabilities: response.vulnerabilities || [],
        syntaxErrors: response.syntaxErrors || [],
        warnings: response.warnings || [],
        databaseValidationMessages: response.databaseValidationMessages || [],
      });
    },
    onError: (error: any) => {
      showNotification(`Validation failed: ${error.response?.data?.message || error.message}`, 'error');
    },
  });

  const testMutation = useMutation({
    mutationFn: (data: any) => queryService.executeRawSql({
      sqlContent: data.sqlContent,
      terminalId: data.terminalId,
      databaseType: data.databaseType,
      connectionId: data.connectionId,
      parameters: data.parameters,
      parameterTypes: data.parameterTypes,
      applicationId: data.applicationId,
    }),
    onSuccess: (response, variables) => {
      setTestResults(response);
      if (response && response.status === 'SUCCESS') {
        const normalizedSql = String(variables?.sqlContent || '').trim().toLowerCase();
        let successMessage = `Test successful! ${response.resultCount} rows returned.`;
        if (normalizedSql.startsWith('create table')) {
          successMessage = 'Test successful! Table created successfully.';
        } else if (normalizedSql.startsWith('alter table')) {
          successMessage = 'Test successful! Table altered successfully.';
        } else if (normalizedSql.startsWith('drop table')) {
          successMessage = 'Test successful! Table dropped successfully.';
        }
        showNotification(successMessage, 'success');
      } else {
        showNotification(`Test failed: ${response?.errorMessage || 'Unknown error'}`, 'error');
      }
    },
    onError: (error: any) => {
      showNotification(`Test execution failed: ${error.message}`, 'error');
      setTestResults({
        status: 'ERROR',
        errorMessage: error.response?.data?.message || error.message
      });
    }
  });

  // Parameter Preparation
  const prepareParameters = () => {
    const parameterMap: Record<string, any> = {};
    const currentParams = parametersRef.current;
    currentParams.forEach(param => {
      if (param.name) {
        let value: any = param.defaultValue || '';
        if (param.type === 'NUMBER') {
          const num = Number(value);
          if (!isNaN(num)) value = num;
        } else if (param.type === 'BOOLEAN') {
          value = String(value).toLowerCase() === 'true';
        }
        parameterMap[param.name] = value;
      }
    });
    return parameterMap;
  };

  // Build a name -> type map so the backend can expand multi-value (STRING_LIST) parameters
  // for ad-hoc test execution (saved queries derive types from stored definitions).
  const prepareParameterTypes = () => {
    const typeMap: Record<string, string> = {};
    parametersRef.current.forEach(param => {
      if (param.name && param.type) {
        typeMap[param.name] = param.type;
      }
    });
    return typeMap;
  };

  const handleTestQuery = async () => {
    const currentData = formDataRef.current;
    const currentSql = String(currentData.sqlContent || '').trim();
    if (!currentSql) {
      showNotification('No SQL to execute', 'warning');
      return;
    }

    if (databaseTypeRef.current === 'EXTERNAL') {
      if (!connectionIdRef.current) {
        showNotification('Please select an external connection before executing.', 'warning');
        return;
      }

      const userRoles = getUserRoles();
      const canUseExternalConnectionFeature =
        hasElevatedAdminAccess(userRoles)
        || hasAnyRole(userRoles, roleConfig.viewServerRegistry)
        || hasAnyRole(userRoles, roleConfig.manageServerRegistry);

      if (!canUseExternalConnectionFeature) {
        showNotification('You do not have access to Application -> External Connection.', 'error');
        return;
      }

      if (isDdlStatement(currentSql)) {
        if (!externalDdlEnabled) {
          showNotification('Selected external connection does not allow DDL operations.', 'error');
          return;
        }

        const canRunExternalDdl =
          hasElevatedAdminAccess(userRoles)
          || hasAnyRole(userRoles, roleConfig.manageServerRegistry);

        if (!canRunExternalDdl) {
          showNotification('Only External Connection admins can execute DDL on external connections.', 'error');
          return;
        }
      }
    }

    const params = prepareParameters();

    // Clear previous results and enter loading state
    setValidationResult(null);
    setTestResults(null);
    setTestExecutionState('validating');

    try {
      // 1. Validate first (calling service directly to avoid triggering ExplainPlanCard in onExecute)
      const valRes = await queryService.validateQuery({
        sqlContent: currentSql,
        terminalId: selectedTerminalRef.current,
        databaseType: databaseTypeRef.current,
        connectionId: databaseTypeRef.current === 'EXTERNAL' ? connectionIdRef.current : undefined,
        parameters: params
      });

      // Set validation result but hide plan lines for regular execution
      setValidationResult({
        ...valRes,
        planLines: []
      });

      if (!valRes.isValid) {
        setTestExecutionState('error');
        showNotification('Validation failed! Check results below the editor.', 'error');
        return; // Stop if invalid
      }

      // Proceed to execute the test
      setTestExecutionState('running');
      try {
        const testRes = await testMutation.mutateAsync({
          sqlContent: currentSql,
          terminalId: selectedTerminalRef.current,
          databaseType: databaseTypeRef.current,
          connectionId: databaseTypeRef.current === 'EXTERNAL' ? connectionIdRef.current : undefined,
          parameters: params,
          parameterTypes: prepareParameterTypes(),
          applicationId: formDataRef.current.application,
        });

        if (testRes && testRes.status === 'SUCCESS') {
          setTestExecutionState('success');
        } else {
          setTestExecutionState('error');
        }
      } catch (testErr: any) {
        setTestExecutionState('error');
      }

    } catch (valErr: any) {
      setTestExecutionState('error');
    }
  };

  const handleExplainPlan = async () => {
    const currentData = formDataRef.current;
    if (!currentData.sqlContent) {
      showNotification('No SQL to explain', 'warning');
      return;
    }

    setIsExplainingPlan(true);
    setValidationResult(null);
    setAiExplanation(null);

    try {
      const params = prepareParameters();
      const result = await queryService.validateQuery({
        sqlContent: currentData.sqlContent,
        terminalId: selectedTerminalRef.current,
        databaseType: databaseTypeRef.current,
        connectionId: databaseTypeRef.current === 'EXTERNAL' ? connectionIdRef.current : undefined,
        parameters: params,
      });

      setValidationResult(result);
      const hasErrors = (result.syntaxErrors && result.syntaxErrors.length > 0) || (result.vulnerabilities && result.vulnerabilities.length > 0);
      if ((!result.planLines || result.planLines.length === 0) && !hasErrors) {
        showNotification('Explain plan could not be generated. Ensure the query is valid and specific to Oracle databases.', 'info');
      }
    } catch (error: any) {
      showNotification(`Failed to fetch explain plan: ${error.message}`, 'error');
    } finally {
      setIsExplainingPlan(false);
    }
  };

  const handleCloseExplainPlan = () => {
    setValidationResult((prev: any) => prev ? { ...prev, planLines: [], syntaxErrors: [], vulnerabilities: [] } : null);
    setAiExplanation(null);
  };

  const handleAiExplainPlan = async () => {
    if (!validationResult?.planLines || isGeneratingAiExplanation) return;

    setIsGeneratingAiExplanation(true);
    try {
      const explanation = await queryService.explainPlanWithAi(
        formDataRef.current.sqlContent,
        validationResult.planLines,
        aiModelRef.current
      );
      setAiExplanation(explanation);
    } catch (error: any) {
      showNotification(`AI explanation failed: ${error.message}`, 'error');
    } finally {
      setIsGeneratingAiExplanation(false);
    }
  };
  const handleAiPopulate = async () => {
    const currentData = formDataRef.current;
    if (!currentData.sqlContent) {
      showNotification('SQL content from Step 1 is required for AI population', 'warning');
      return;
    }

    setIsAiPopulating(true);
    try {
      const response = await queryService.generateMetadata({
        sqlContent: currentData.sqlContent,
        databaseType: databaseType as any
      });

      if (response) {
        let generatedQueryId = response.queryId || formDataRef.current.queryId;
        if (generatedQueryId) {
          generatedQueryId = formatQueryId(generatedQueryId);
        }
        const enhancedFormData = {
          ...formDataRef.current,
          queryId: generatedQueryId,
          name: response.name || formDataRef.current.name,
          description: response.description || formDataRef.current.description,
          category: response.category || formDataRef.current.category,
          tags: response.tags ? Array.from(new Set([...formDataRef.current.tags, ...response.tags])) : formDataRef.current.tags
        };
        formDataRef.current = enhancedFormData;
        setFormData(enhancedFormData);
        showNotification('Query metadata populated successfully!', 'success');
      }
    } catch (error: any) {
      console.error('AI Metadata generation failed:', error);
      showNotification(`Failed to populate metadata: ${error.response?.data?.message || error.message}`, 'error');
    } finally {
      setIsAiPopulating(false);
    }
  };

  // Field Validation
  const validateField = (name: string, value: any): string => {
    switch (name) {
      case 'queryId':
        if (!value) return 'Query ID is required';
        if (!validateQueryId(value)) return 'Invalid ID format';
        if (value.length < 3) return 'Min 3 chars';
        return '';
      case 'name':
        if (!value) return 'Name is required';
        return '';
      case 'sqlContent':
        if (!value) return 'SQL is required';
        return '';
      case 'category':
        if (!value) return 'Category is required';
        return '';
      case 'application':
        if (!value) return 'Application is required';
        return '';
      default: return '';
    }
  };

  const handleFieldChange = (name: string, value: any) => {
    console.log(`DEBUG: handleFieldChange for ${name}:`, value);
    let finalValue = value;
    if (name === 'queryId' && typeof value === 'string') {
      finalValue = formatQueryId(value);
    }
    formDataRef.current = { ...formDataRef.current, [name]: finalValue };
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (name === 'sqlContent') {
      setValidationResult(null);
      // Clear AI correction diff when user manually edits the SQL
      if (aiCorrectionDiff && value !== aiCorrectionDiff.corrected) {
        setAiCorrectionDiff(null);
      }
    }
  };

  const handleBlur = (name: string) => {
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, formData[name as keyof typeof formData]);
    if (error) setErrors((prev) => ({ ...prev, [name]: error }));
  };

  // Tag Management
  const tagInputRef = useRef<any>(null);

  const handleAddTag = (tagToAdd?: string) => {
    const val = tagToAdd || currentTag;
    const sanitized = sanitizeInput(val).trim().toLowerCase();
    if (sanitized && !formDataRef.current.tags.includes(sanitized)) {
      if (formDataRef.current.tags.length < 10) {
        formDataRef.current = { ...formDataRef.current, tags: [...formDataRef.current.tags, sanitized] };
        setFormData(formDataRef.current);
        setCurrentTag('');
      }
    }
  };

  const handleAddTagClick = () => {
    const val = tagInputRef.current?.value || currentTag;
    handleAddTag(val);
  };

  const handleRemoveTag = (tagToRemove: string) => {
    formDataRef.current = {
      ...formDataRef.current,
      tags: formDataRef.current.tags.filter((tag) => tag !== tagToRemove),
    };
    setFormData(formDataRef.current);
  };

  // Parameter Management
  const handleAddParameter = () => {
    const newParams = [...parametersRef.current, { name: '', type: 'STRING', required: true, defaultValue: '', description: '' }];
    parametersRef.current = newParams;
    setParameters(newParams);
  };
  const handleRemoveParameter = (index: number) => {
    const newParams = parametersRef.current.filter((_, i) => i !== index);
    parametersRef.current = newParams;
    setParameters(newParams);
  };
  const handleParameterChange = (index: number, field: string, value: any) => {
    const newParams = parametersRef.current.map((p, i) => i === index ? { ...p, [field]: value } : p);
    parametersRef.current = newParams;
    setParameters(newParams);
    setValidationResult(null);
  };

  console.log('DEBUG: Render activeStep:', activeStep);

  // Navigation Handlers
  const handleNext = () => {
    const currentData = formDataRef.current;
    console.log('DEBUG: handleNext called. ActiveStep (State):', activeStep, 'FormData (Ref):', currentData);

    if (activeStep === 0) {
      if (!currentData.sqlContent) {
        console.warn('DEBUG: Validation failed - SQL Content missing');
        setErrors(prev => ({ ...prev, sqlContent: 'SQL Content is required' }));
        setTouched(prev => ({ ...prev, sqlContent: true }));
        showNotification('SQL Content is required', 'error');
        return;
      }
    }

    setActiveStep(prev => {
      const next = Math.min(prev + 1, steps.length - 1);
      console.log('DEBUG: Updating activeStep from', prev, 'to', next);
      return next;
    });
  };
  const handleBack = () => setActiveStep(prev => Math.max(prev - 1, 0));
  const handleCancel = () => navigate('/queries');

  const handleSelectQueryFromSidebar = (queryId: string) => {
    setSelectedQueryId(queryId);
    setSidebarOpen(false); // Close sidebar after selection
  };

  const handleCreateNewQuery = () => {
    setSelectedQueryId(null);
    const resetFormData = {
      queryId: '',
      name: '',
      description: '',
      sqlContent: '',
      category: '',
      application: '',
      tags: [],
      changeNotes: '',
      createdBy: 'current-user',
      makeCurrent: true,
    };
    formDataRef.current = resetFormData;
    setFormData(resetFormData);
    parametersRef.current = [];
    setParameters([]);
    setTestResults(null);
    setValidationResult(null);
    setTestExecutionState('idle');
    setActiveResultTab('results');
    setSidebarOpen(false);
  };

  // Submit Handler
  const handleSubmit = () => {
    if (createMutation.isPending) return;
    const currentData = formDataRef.current;
    const currentParams = parametersRef.current;
    // Final full validation
    const newErrors: Record<string, string> = {};
    ['queryId', 'name', 'category', 'application', 'sqlContent'].forEach(f => {
      const err = validateField(f, currentData[f as keyof typeof currentData]);
      if (err) newErrors[f] = err;
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      setTouched({ queryId: true, name: true, category: true, application: true });
      showNotification('Please fix errors', 'error');
      return;
    }

    handleFieldChange('makeCurrent', true); // Default to true when opening dialog
    setShowConfirmDialog(true);
  };

  const handleConfirmCreation = () => {
    if (createMutation.isPending) return;
    const currentData = formDataRef.current;
    const currentParams = parametersRef.current;

    const payload = {
      ...currentData,
      applicationId: currentData.application,
      description: sanitizeInput(currentData.description),
      sqlContent: currentData.sqlContent.trim(),
      parameters: currentParams.filter(p => p.name && p.type),
      makeCurrent: currentData.makeCurrent,
    };

    console.log('[CreateQueryPage] Submitting creation mutation with payload:', payload);
    createMutation.mutate(payload);
    setShowConfirmDialog(false);
  };

  return (
    <>
      {/* Query List Sidebar — fixed positioned, lives outside the push wrapper */}
      <QueryListSidebar
        isOpen={sidebarOpen}
        onSelectQuery={handleSelectQueryFromSidebar}
        selectedQueryId={selectedQueryId}
        onCreateNew={handleCreateNewQuery}
      />

      {/* Push-wrapper: shifts all content right when sidebar is open */}
      <div style={{ marginLeft: sidebarOpen ? querySidebarPush : '0px', transition: 'margin-left 0.3s ease' }}>

      {/* Create Query Header removed to save vertical space per user request */}

      <div style={{ marginBottom: 12, padding: '0 16px' }}>
        <McStepIndicator labels={steps} currentindex={activeStep} />
      </div>

      {/* Navigation toolbar — only shown for steps > 0 (step 0 has its own integrated top bar) */}
      {activeStep > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          marginBottom: 12,
          background: 'var(--mds-color-neutral-weakest-background, #f8f8f8)',
          borderRadius: 8,
          padding: '0 16px',
          boxShadow: '0 1px 4px rgba(0,0,0,0.03)',
          minHeight: 44,
        }}>
          <McButton
            fit="small"
            variant="outlined"
            appearance={sidebarOpen ? 'primary' : 'neutral'}
            style={{ padding: '0 10px', fontSize: '12px', gap: 4 }}
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            <McIcon icon="sliders-horizontal" slot="prefix" />
            {sidebarOpen ? 'Hide queries' : 'Queries'}
          </McButton>

          <McButton variant="plain" style={{ minWidth: 36, padding: "0 8px" }} appearance="primary" onClick={handleBack} fit="small">
            <McIcon icon="arrow-left" slot="prefix" />
          </McButton>
          <div style={{ flex: 1 }} />
          {activeStep < steps.length - 1 ? (
            <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center" key={`next-${activeStep}`}>
              {/* @ts-ignore */}
              <McButton slot="trigger" variant="filled" appearance="primary" onClick={handleNext} fit="small" label='Go to next step'>
                Next
                <McIcon icon="arrow-right" slot="suffix" />
              </McButton>
              <span>Go to next step</span>
            </McTooltip>
          ) : (
            <McButton
              variant="filled"
              appearance="primary"
              onClick={handleSubmit}
              disabled={createMutation.isPending}
              fit="small"
              key="submit"
            >
              {createMutation.isPending ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="floppy-disk" slot="prefix" />}
              {createMutation.isPending ? 'Creating...' : 'Create Query'}
            </McButton>
          )}
        </div>
      )}

      {/* Step 0: SQL Editor */}
      {activeStep === 0 && (
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100vh - 240px)',
          minHeight: 520,
          border: '1px solid #e5e7eb',
          borderRadius: 8,
          overflow: 'hidden',
          backgroundColor: '#fff',
        }}>
          {/* TOP BAR + OVERLAY HOST */}
          <div ref={schemaOverlayHostRef} style={{ position: 'relative', flexShrink: 0, zIndex: 5 }}>
          <div style={{ display: 'flex', alignItems: 'center', minHeight: 56, flexShrink: 0, borderBottom: '1px solid #e5e7eb', padding: '6px 8px', gap: 8, backgroundColor: '#f9fafb', flexWrap: 'wrap' }}>
            <McButton
              fit="small"
              variant="outlined"
              appearance={sidebarOpen ? 'primary' : 'neutral'}
              style={{ padding: '0 10px', fontSize: '12px', gap: 4 }}
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <McIcon icon="sliders-horizontal" slot="prefix" />
              {sidebarOpen ? 'Hide queries' : 'Queries'}
            </McButton>
            <div style={{ width: 130, flexShrink: 0 }}>
              <DatabaseSelect label="" placeholder="DB Type" value={databaseType} onChange={(val) => { setDatabaseType(val); databaseTypeRef.current = val; setValidationResult(null); if (val !== 'EXTERNAL') { setConnectionId(''); connectionIdRef.current = ''; setExternalDdlEnabled(false); setExternalConnectionServerType(undefined); } }} disabled={aiGenerateMutation.isPending} />
            </div>
            {databaseType === 'EXTERNAL' ? (
              <div style={{ width: 260, flexShrink: 0 }}>
                <ConnectionSelect label="Connection" placeholder="Select Connection" value={connectionId} onChange={(id, conn) => { setConnectionId(id); connectionIdRef.current = id; setValidationResult(null); setExternalDdlEnabled(conn?.allowedOps === 'DDL'); setExternalConnectionServerType(conn?.serverType); }} onResolvedConnection={(conn) => { setExternalDdlEnabled(conn?.allowedOps === 'DDL'); setExternalConnectionServerType(conn?.serverType); }} disabled={aiGenerateMutation.isPending} />
              </div>
            ) : (
              <div style={{ width: 220, flexShrink: 0 }}>
                <TerminalSelect label="" placeholder="Select Terminal" value={selectedTerminal} onChange={(val) => { setSelectedTerminal(val); setValidationResult(null); setSchemaExplorerWarning(null); if (!val) setSchemaPanelOpen(false); }} disabled={aiGenerateMutation.isPending} />
              </div>
            )}

            {/* Center: schema tab shortcuts */}
            <div style={{ flex: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <McTabBar
                currentindex={schemaPanelOpen ? (schemaPanelTab === 'schema' ? 0 : schemaPanelTab === 'field-search' ? 1 : 2) : undefined}
                tabchange={(e: any) => {
                  const idx = e.detail;
                  openSchemaPanelTab(idx === 0 ? 'schema' : idx === 1 ? 'field-search' : 'field-catalog');
                }}
              >
                <McTab slot="tab" label="" icon="table" title="Schema Browser" />
                <McTab slot="tab" label="" icon="magnifying-glass" title="Field Intelligence" />
                <McTab slot="tab" label="" icon="tag" title="Field Catalog" />
              </McTabBar>
            </div>

            <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small"
                  variant={(testExecutionState !== 'validating' && testExecutionState !== 'running' && !aiGenerateMutation.isPending && (databaseType === 'EXTERNAL' ? !!connectionId : !!selectedTerminal)) ? 'filled' : 'plain'} style={{ minWidth: 36, padding: '0 8px' }}
                  appearance="neutral"
                  disabled={testExecutionState === 'validating' || testExecutionState === 'running' || aiGenerateMutation.isPending || (databaseType === 'EXTERNAL' ? !connectionId : !selectedTerminal)}
                  onClick={() => { setActiveResultTab('results'); handleTestQuery(); }}
                >
                  {(testExecutionState === 'validating' || testExecutionState === 'running') ? <McLoadingIndicator fit="small" /> : <McIcon icon="paper-plane" />}
                </McButton>
                <span>{testExecutionState === 'validating' ? 'Checking…' : testExecutionState === 'running' ? 'Running…' : 'Execute query'}</span>
              </McTooltip>
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                  disabled={isExplainingPlan || aiGenerateMutation.isPending || (databaseType === 'EXTERNAL' ? !connectionId : !selectedTerminal)}
                  onClick={() => { setActiveResultTab('explain'); handleExplainPlan(); }}
                >
                  {isExplainingPlan ? <McLoadingIndicator fit="small" /> : <McIcon icon="lightning" />}
                </McButton>
                <span>Explain query plan</span>
              </McTooltip>
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }} disabled={aiGenerateMutation.isPending} onClick={() => setAiPanelOpen(true)}>
                  <McIcon icon="chatbot" />
                </McButton>
                <span>AI Assistant</span>
              </McTooltip>
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small" variant={schemaPanelOpen ? 'filled' : 'plain'} appearance={schemaPanelOpen ? 'primary' : undefined} style={{ minWidth: 36, padding: "0 8px" }} aria-pressed={schemaPanelOpen} onClick={toggleSchemaPanel}>
                  <McIcon icon="database" />
                </McButton>
                <span>{schemaPanelOpen ? 'Hide schema panel' : 'Show schema panel'}</span>
              </McTooltip>
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small" variant="plain" style={{ minWidth: 36, padding: "0 8px" }} disabled={aiGenerateMutation.isPending} onClick={() => setShowSchemaExplorer(true)}>
                  <McIcon icon="table" />
                </McButton>
                <span>Schema explorer (fullscreen)</span>
              </McTooltip>
              <div style={{ width: 1, height: 20, backgroundColor: '#e5e7eb', margin: '0 4px', flexShrink: 0 }} />
              <McTooltip onMouseOver={(e: any) => { e.currentTarget.open = true; }} onMouseOut={(e: any) => { e.currentTarget.open = false; }} position="bottom-center">
                {/* @ts-ignore */}
                <McButton slot="trigger" fit="small" variant="plain" appearance="primary" onClick={handleNext}>
                  <McIcon icon="arrow-right" />
                </McButton>
                <span>Go to next step</span>
              </McTooltip>
            </div>
          </div>

          {schemaExplorerWarning && (
            <div style={{
              margin: '8px 8px 0',
              padding: '8px 12px',
              border: '1px solid #90caf9',
              borderRadius: 8,
              background: '#e3f2fd',
              color: '#0d47a1',
              fontSize: 12,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}>
              <McIcon icon="info-circle" style={{ fontSize: 14, flexShrink: 0 }} />
              <span>{schemaExplorerWarning}</span>
            </div>
          )}

          {/* SCHEMA PANEL OVERLAY (below toolbar) */}
          {schemaPanelOpen && (
            <>
              <div
                onClick={() => setSchemaPanelOpen(false)}
                style={{
                  position: 'fixed',
                  top: schemaBackdropTop,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: 1090,
                  background: 'rgba(15, 23, 42, 0.06)',
                }}
              />
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                zIndex: 1100,
                height: 'min(560px, calc(100vh - 180px))',
                border: '1px solid #e5e7eb',
                borderRadius: 0,
                background: '#f8f9f6',
                boxShadow: 'none',
                overflow: 'hidden',
                padding: '8px 12px 12px',
              }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 4 }}>
                  <McButton fit="small" variant="plain" onClick={() => setSchemaPanelOpen(false)}>
                    <McIcon icon="times" />
                  </McButton>
                </div>
                <SchemaExplorer
                  open={true}
                  onClose={() => setSchemaPanelOpen(false)}
                  terminalId={selectedTerminal}
                  databaseType={databaseType}
                  renderMode="embedded"
                  defaultTab={schemaPanelTab}
                  onInsertSnippet={(text) => {
                    const editor = sqlEditorRef.current;
                    if (!editor) {
                      navigator.clipboard.writeText(text);
                      showNotification(`Copied ${text}`, 'info');
                      return;
                    }
                    const sel = editor.getSelection();
                    const prevChar = formData.sqlContent[sel.from - 1];
                    const snippet = applyLeadingSpace(text, prevChar);
                    editor.insertAtCursor(snippet);
                    showNotification(`Inserted ${text}`, 'success');
                  }}
                />
              </div>
            </>
          )}
          </div>

          {/* MAIN AREA: vertical split */}
          <div ref={editorColumnRef} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Tip banner */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 12px', backgroundColor: '#fff8e1', borderBottom: '1px solid #ffe082', fontSize: 11, color: '#141414', flexShrink: 0 }}>
              <McIcon icon="shield" style={{ fontSize: 12, color: '#f9a825', flexShrink: 0 }} />
              <span>
                <strong>Tip:</strong> Use <code>:paramName</code> for dynamic parameters. Limit results with{' '}
                {databaseType === 'POSTGRESQL' ? <code>LIMIT 10</code> : <code>WHERE ROWNUM &lt;= 10</code>} for best performance.
              </span>
            </div>

            {/* SQL Editor */}
            <div style={{ flex: editorRatio, overflow: 'auto', minHeight: 80, display: 'flex', flexDirection: 'column' }}>
              <div style={{ flex: 1, minHeight: 200 }}>
                <SqlEditor
                  ref={sqlEditorRef}
                  value={formData.sqlContent}
                  onChange={(value) => handleFieldChange('sqlContent', value)}
                  terminalId={selectedTerminal}
                  databaseType={databaseType}
                  externalDdlEnabled={databaseType === 'EXTERNAL' ? externalDdlEnabled : true}
                  externalConnectionServerType={externalConnectionServerType}
                  readOnly={aiGenerateMutation.isPending}
                />
              </div>
            </div>

            {/* Row Resizer */}
            <div
              onMouseDown={handleResizerMouseDown}
              style={{ height: 8, cursor: 'row-resize', backgroundColor: '#f3f4f6', borderTop: '1px solid #e5e7eb', borderBottom: '1px solid #e5e7eb', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            >
              <div style={{ width: 32, height: 3, borderRadius: 2, backgroundColor: '#d1d5db' }} />
            </div>

            {/* Results panel */}
            <div style={{ flex: 1 - editorRatio, display: 'flex', flexDirection: 'column', overflow: 'hidden', minHeight: 80 }}>
              {/* Tab bar */}
              <div style={{ display: 'flex', alignItems: 'center', height: 36, borderBottom: '1px solid #e5e7eb', flexShrink: 0, paddingLeft: 8, gap: 4, backgroundColor: '#f9fafb' }}>
                {(['results', 'explain'] as const).map((tab) => (
                  <button
                    key={tab}
                    onClick={() => setActiveResultTab(tab)}
                    style={{ height: 36, padding: '0 14px', background: 'transparent', border: 'none', borderBottom: activeResultTab === tab ? '2px solid #0073ab' : '2px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: activeResultTab === tab ? 600 : 400, color: activeResultTab === tab ? '#0073ab' : '#6b7280', transition: 'color 0.15s' }}
                  >
                    {tab === 'results' ? 'Results' : 'Explain Plan'}
                  </button>
                ))}
                {testResults?.data?.length ? (
                  <button
                    onClick={() => setActiveResultTab('visualization')}
                    style={{ height: 36, padding: '0 14px', background: 'transparent', border: 'none', borderBottom: activeResultTab === 'visualization' ? '2px solid #0073ab' : '2px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: activeResultTab === 'visualization' ? 600 : 400, color: activeResultTab === 'visualization' ? '#0073ab' : '#6b7280', transition: 'color 0.15s' }}
                  >
                    Visualization
                  </button>
                ) : null}
                {parameters.length > 0 && (
                  <button
                    onClick={() => setActiveResultTab('parameters')}
                    style={{ height: 36, padding: '0 14px', background: 'transparent', border: 'none', borderBottom: activeResultTab === 'parameters' ? '2px solid #0073ab' : '2px solid transparent', cursor: 'pointer', fontSize: 12, fontWeight: activeResultTab === 'parameters' ? 600 : 400, color: activeResultTab === 'parameters' ? '#0073ab' : '#6b7280', transition: 'color 0.15s' }}
                  >
                    Parameters ({parameters.length})
                  </button>
                )}
                {activeResultTab === 'results' && testResults?.status === 'SUCCESS' && testResults.data && (
                  <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, paddingRight: 8 }}>
                    <span style={{ fontSize: 11, color: '#6b7280' }}>{filteredTestResults.length}/{testResults.resultCount} rows</span>
                  </div>
                )}
              </div>
              {/* Tab content */}
              <div style={{ flex: 1, overflow: 'auto', padding: '8px 16px' }}>
                {activeResultTab === 'results' && (
                  <>
                    {testExecutionState === 'idle' && !validationResult && !aiCorrectionDiff && (
                      <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 24 }}>
                        <McIcon icon="paper-plane" style={{ fontSize: 32, opacity: 0.4, display: 'block', margin: '0 auto 8px' }} />
                        <p style={{ margin: 0, fontSize: 13 }}>Execute the query to see results</p>
                      </div>
                    )}
                    {(testExecutionState === 'validating' || testExecutionState === 'running') && (
                      <div style={{ textAlign: 'center', color: '#9ca3af', marginTop: 24 }}>
                        <McLoadingIndicator style={{ display: 'block', margin: '0 auto 8px' }} />
                        <p style={{ margin: 0, fontSize: 13 }}>{testExecutionState === 'validating' ? 'Validating SQL…' : 'Running query…'}</p>
                      </div>
                    )}
                    {aiCorrectionDiff && (
                      <McNotification
                        appearance="success"
                        heading="SQL corrected by AI"
                        style={{ marginBottom: 16 }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                          <span>Review the changes before executing.</span>
                          <McButton
                            variant="filled"
                            appearance="secondary"
                            fit="small"
                            onClick={() => setShowDiffDialog(true)}
                          >
                            <McIcon icon="eye" slot="prefix" />
                            View Changes
                          </McButton>
                          <McButton
                            variant="plain" style={{ minWidth: 36, padding: "0 8px" }}
                            fit="small"
                            onClick={() => {
                              handleFieldChange('sqlContent', aiCorrectionDiff.original);
                              setAiCorrectionDiff(null);
                            }}
                          >
                            <McIcon icon="arrow-anti-clockwise" slot="prefix" />
                            Undo
                          </McButton>
                        </div>
                      </McNotification>
                    )}
                    {validationResult && (!testResults || testResults.status !== 'SUCCESS') && (
                      <McNotification
                        appearance={validationResult.isValid ? "success" : "error"}
                        heading={validationResult.isValid ? "SQL Valid" : "Execution Failed"}
                        style={{ marginBottom: 8 }}
                      >
                        {!validationResult.isValid && (validationResult.syntaxErrors?.length > 0 || validationResult.error) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {validationResult.syntaxErrors?.map((err: string, idx: number) => (
                              <div key={idx} style={{ fontWeight: 500 }}>{err}</div>
                            ))}
                            {validationResult.error && <div>{validationResult.error}</div>}
                            {validationResult.databaseValidationMessages?.map((msg: string, idx: number) => (
                              <div key={idx} style={{ fontSize: '0.85em', opacity: 0.8, marginTop: 4 }}>{msg}</div>
                            ))}
                          </div>
                        ) : (
                          validationResult.isValid ? "Syntax check passed and query is valid." : "Validation failed with unknown error."
                        )}
                        {!validationResult.isValid && (() => {
                          const errClass = classifyError();
                          return errClass.fixable ? (
                            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <McButton
                                variant="filled"
                                appearance="secondary"
                                fit="small"
                                onClick={handleAutoFixSql}
                                disabled={autoFixSqlMutation.isPending}
                              >
                                {autoFixSqlMutation.isPending ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="wrench" slot="prefix" />}
                                Try Auto Fix
                              </McButton>
                              <div style={{ display: 'flex', gap: 0, borderRadius: 999, overflow: 'hidden', border: '1px solid var(--mds-color-neutral-passive-background, #d6d6d6)' }}>
                                {aiModelOptions.map((opt: any) => (
                                  <button
                                    key={opt.value}
                                    onClick={() => setAutoFixModel(opt.value)}
                                    style={{ padding: '4px 12px', fontSize: '0.78rem', fontWeight: autoFixModel === opt.value ? 600 : 400, cursor: 'pointer', border: 'none', background: autoFixModel === opt.value ? 'var(--mds-color-interactive-enabled-background, #0073ab)' : 'transparent', color: autoFixModel === opt.value ? '#fff' : 'var(--mds-color-neutral-strong-text, #141414)', transition: 'background 0.15s, color 0.15s', whiteSpace: 'nowrap' }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 10, fontSize: '0.85em', color: 'var(--mds-color-neutral-weak-text, #6b6b6b)' }}>
                              <McIcon icon="shield" style={{ fontSize: 14, marginRight: 4, verticalAlign: 'middle' }} />
                              {errClass.reason}
                            </div>
                          );
                        })()}
                      </McNotification>
                    )}
                    {testResults && testResults.status === 'ERROR' && (
                      <McNotification appearance="error" heading="Execution Error" style={{ marginBottom: 12 }}>
                        <div>{testResults.errorMessage || 'Query execution failed.'}</div>
                        {(() => {
                          const errClass = classifyError();
                          return errClass.fixable ? (
                            <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8 }}>
                              <McButton
                                variant="filled"
                                appearance="secondary"
                                fit="small"
                                onClick={handleAutoFixSql}
                                disabled={autoFixSqlMutation.isPending}
                              >
                                {autoFixSqlMutation.isPending ? <McLoadingIndicator slot="prefix" fit="small" /> : <McIcon icon="wrench" slot="prefix" />}
                                Try Auto Fix
                              </McButton>
                              <div style={{ display: 'flex', gap: 0, borderRadius: 999, overflow: 'hidden', border: '1px solid var(--mds-color-neutral-passive-background, #d6d6d6)' }}>
                                {aiModelOptions.map((opt: any) => (
                                  <button
                                    key={opt.value}
                                    onClick={() => setAutoFixModel(opt.value)}
                                    style={{ padding: '4px 12px', fontSize: '0.78rem', fontWeight: autoFixModel === opt.value ? 600 : 400, cursor: 'pointer', border: 'none', background: autoFixModel === opt.value ? 'var(--mds-color-interactive-enabled-background, #0073ab)' : 'transparent', color: autoFixModel === opt.value ? '#fff' : 'var(--mds-color-neutral-strong-text, #141414)', transition: 'background 0.15s, color 0.15s', whiteSpace: 'nowrap' }}
                                  >
                                    {opt.label}
                                  </button>
                                ))}
                              </div>
                            </div>
                          ) : (
                            <div style={{ marginTop: 10, fontSize: '0.85em', color: 'var(--mds-color-neutral-weak-text, #6b6b6b)' }}>
                              <McIcon icon="shield" style={{ fontSize: 14, marginRight: 4, verticalAlign: 'middle' }} />
                              {errClass.reason}
                            </div>
                          );
                        })()}
                      </McNotification>
                    )}
                    {testResults && testResults.status === 'SUCCESS' && testResults.data && (
                      <ResultsVisualization
                        data={filteredTestResults}
                        columns={resultColumns.map((col: any) => String(col?.id || col?.label || ''))}
                        sqlQuery={formData.sqlContent}
                        onExport={(format: any) => {
                          if (format === 'csv') {
                            // Use existing download functionality
                            const button = document.querySelector('[data-testid="download-results"]') as HTMLButtonElement;
                            button?.click();
                          } else {
                            // For JSON, create a download
                            const dataStr = JSON.stringify(filteredTestResults, null, 2);
                            const dataUri = 'data:application/json;charset=utf-8,'+ encodeURIComponent(dataStr);
                            const exportFileDefaultName = `query-results-${formData.queryId || 'untitled'}.json`;
                            const linkElement = document.createElement('a');
                            linkElement.setAttribute('href', dataUri);
                            linkElement.setAttribute('download', exportFileDefaultName);
                            linkElement.click();
                          }
                        }}
                      />
                    )}
                  </>
                )}
                {activeResultTab === 'explain' && (
                  <ExplainPlanCard
                    planLines={validationResult?.planLines || []}
                    isExplaining={isExplainingPlan}
                    aiExplanation={aiExplanation}
                    isGeneratingAiExplanation={isGeneratingAiExplanation}
                    validationErrors={[
                      ...(validationResult?.syntaxErrors || []),
                      ...(validationResult?.vulnerabilities?.map((v: any) => v.description) || [])
                    ]}
                    onAiExplain={handleAiExplainPlan}
                    onClose={handleCloseExplainPlan}
                    aiModelOptions={aiModelOptions}
                    selectedModel={aiModelName}
                    onModelChange={setAiModelName}
                    onAutoFix={handleAutoFixSql}
                    isAutoFixing={autoFixSqlMutation.isPending}
                    autoFixModel={autoFixModel}
                    autoFixModelOptions={aiModelOptions}
                    onAutoFixModelChange={setAutoFixModel}
                    unfixableReason={(() => { const c = classifyError(); return c.fixable ? undefined : c.reason; })()}
                  />
                )}
                {activeResultTab === 'visualization' && (
                  <ChartVisualization data={filteredTestResults?.length ? filteredTestResults : (testResults?.data || [])} />
                )}
                {activeResultTab === 'parameters' && (
                  <div>
                    {parameters.map((param, index) => (
                      <ParameterRow
                        key={index}
                        name={param.name}
                        type={param.type}
                        testValue={param.defaultValue}
                        description={param.description}
                        onTypeChange={(v) => handleParameterChange(index, 'type', v)}
                        onTestValueChange={(v) => handleParameterChange(index, 'defaultValue', v)}
                        onDescriptionChange={(v) => handleParameterChange(index, 'description', v)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Floating Assistant (AI chat) */}
      <FloatingAssistant
        open={aiPanelOpen}
        onOpen={() => setAiPanelOpen(true)}
        onClose={() => setAiPanelOpen(false)}
        hideLauncher={true}
        chatHistory={chatHistory}
        setChatHistory={setChatHistory}
        aiIntent={aiIntent}
        setAiIntent={setAiIntent}
        aiDomainOverride={aiDomainOverride}
        setAiDomainOverride={setAiDomainOverride}
        aiDomainOptions={aiDomainOptions}
        aiModelName={aiModelName}
        setAiModelName={setAiModelName}
        aiModelOptions={aiModelOptions}
        aiGenerateMutation={aiGenerateMutation}
        handleAiGenerate={handleAiGenerate}
        onRetryMessage={handleAiRetryMessage}
        chatScrollRef={chatScrollRef}
        selectedTerminal={selectedTerminal}
        setSelectedTerminal={setSelectedTerminal}
        databaseType={databaseType}
        setDatabaseType={setDatabaseType}
      />





      {/* Step 2: Finalize */}
      {activeStep === 1 && (
        <McCard>
          <div style={{ padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 className="mds-headline--xx-small-bold">Basic Information</h2>
              <McButton
                label='Populate with AI'
                icon="chatbot"
                appearance="secondary"
                fit="medium"
                variant='filled'
                onClick={handleAiPopulate}
                disabled={isAiPopulating}
              >
                {isAiPopulating ? <McLoadingIndicator slot="prefix" fit="small" /> : null}
              </McButton>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
              <McInput
                label="Query ID"
                required
                value={formData.queryId}
                onInput={(e: any) => handleFieldChange('queryId', e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                invalid={touched.queryId && !!errors.queryId}
                errormessage={errors.queryId}
              >
                <McIcon icon="file" slot="prefix" />
              </McInput>

              <McInput
                label="Name"
                required
                value={formData.name}
                onInput={(e: any) => handleFieldChange('name', e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                invalid={touched.name && !!errors.name}
                errormessage={touched.name && !!errors.name ? 'Name is required' : ''}
              />

              <McSelect
                label="Application"
                required
                value={formData.application}
                data={activeApplications}
                optionselected={(e: any) => handleFieldChange('application', e.detail.value)}
                invalid={touched.application && !!errors.application}
                errormessage={errors.application}
                disabled={activeApplications.length === 0}
                placeholder={activeApplications.length === 0 ? "You don't have access to any applications" : "Select application"}
              />

              <McSelect
                label="Category"
                required
                value={formData.category}
                data={getCategoryOptions()}
                optionselected={(e: any) => handleFieldChange('category', e.detail.value)}
              />

              <McTextarea
                label="Description"
                rows={3}
                value={formData.description}
                onInput={(e: any) => {
                  handleFieldChange('description', e.detail?.value !== undefined ? e.detail.value : (e.target.value || ''));
                  handleAutoExpand(e);
                }}
              />

              <div>
                <span className="mds-text--small-bold" style={{ display: 'block', marginBottom: '8px' }}>Tags</span>
                <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
                  <div style={{ flexGrow: 1 }}>
                    <McInput
                      ref={tagInputRef}
                      placeholder="Add tag"
                      value={currentTag}
                      onInput={(e: any) => setCurrentTag(e.detail?.value !== undefined ? e.detail.value : e.target.value)}
                      onKeyDown={(e: any) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddTagClick();
                        }
                      }}
                    />
                  </div>
                  <McButton variant="plain" style={{ minWidth: 36, padding: "0 8px" }} appearance="primary" onClick={handleAddTagClick}>
                    <McIcon icon="plus" slot="prefix" />
                    Add Tag
                  </McButton>
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {formData.tags.map(tag => (
                    <McTag key={tag} label={tag} dismiss={() => handleRemoveTag(tag)} withaction appearance="neutral-default" />
                  ))}
                  {formData.tags.length === 0 && (
                    <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-weaker-text)' }}>No tags added.</span>
                  )}
                </div>
              </div>

              <McTextarea
                label="Change Notes"
                rows={2}
                value={formData.changeNotes}
                onInput={(e: any) => {
                  handleFieldChange('changeNotes', e.detail?.value !== undefined ? e.detail.value : (e.target.value || ''));
                  handleAutoExpand(e);
                }}
              />

              <div style={{ 
                marginTop: '8px', 
                padding: '16px', 
                backgroundColor: 'var(--mds-color-neutral-weakest-background)', 
                borderRadius: '8px', 
                border: '1px dotted var(--mds-color-neutral-weak-decoration)' 
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px' }}>
                  <McIcon icon="lightning" style={{ color: 'var(--mds-color-warning-main-background, #f9a825)', fontSize: '14px' }} />
                  <h4 className="mds-text--x-small-bold" style={{ margin: 0 }}>Version Activation</h4>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input 
                    type="checkbox" 
                    checked={formData.makeCurrent} 
                    onChange={(e) => handleFieldChange('makeCurrent', e.target.checked)} 
                    style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                  />
                  <span className="mds-text--small-bold">Make this version current (Active in Production)</span>
                </label>
                {!formData.makeCurrent && (
                  <p className="mds-text--xx-small-regular" style={{ color: 'var(--mds-color-error-main-background)', marginTop: '4px', marginLeft: '24px' }}>
                    Warning: Saving as draft will not update the production version.
                  </p>
                )}
              </div>
            </div>
          </div>
        </McCard>
      )}

      </div>{/* end push-wrapper */}

      {/* Utilities */}
      <SchemaExplorer
        open={showSchemaExplorer}
        onClose={() => setShowSchemaExplorer(false)}
        terminalId={selectedTerminal}
        databaseType={databaseType}
      />

      <ConfirmationDialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
        onConfirm={handleConfirmCreation}
        title="Confirm Query Creation"
        content={
          <div>
            <p>Are you sure you want to create this query?</p>
            <ul style={{ marginTop: '8px', listStyleType: 'disc', paddingLeft: '20px' }}>
              <li><strong>ID:</strong> {formData.queryId}</li>
              <li><strong>Name:</strong> {formData.name}</li>
              <li><strong>Application:</strong> {formData.application}</li>
            </ul>
            <div style={{ marginTop: '24px', padding: '16px', backgroundColor: 'var(--mds-color-neutral-weakest-background)', borderRadius: '8px', border: '1px solid var(--mds-color-neutral-weak-decoration)' }}>
              <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                <input 
                  type="checkbox" 
                  checked={formData.makeCurrent} 
                  onChange={(e) => handleFieldChange('makeCurrent', e.target.checked)} 
                  style={{ marginRight: '8px', width: '16px', height: '16px', cursor: 'pointer' }}
                />
                <span className="mds-text--medium-bold">Make this version current (Active in Production)?</span>
              </label>
              {!formData.makeCurrent && (
                <div style={{ display: 'flex', alignItems: 'flex-start', marginTop: '12px', padding: '8px 12px', backgroundColor: 'var(--mds-color-warning-weakest-background, #fff8e1)', borderRadius: '6px', border: '1px solid var(--mds-color-warning-weak-decoration, #ffe082)' }}>
                  <McIcon icon="times-circle" style={{ color: 'var(--mds-color-warning-main-background, #f9a825)', marginRight: '8px', marginTop: '2px' }} />
                  <span className="mds-text--small-regular" style={{ color: 'var(--mds-color-neutral-strong-text)' }}>
                    <strong>Warning:</strong> Unless it is marked as current, this query will be saved as a draft and will not be served in production.
                  </span>
                </div>
              )}
            </div>
          </div>
        }
        confirmLabel="Create Query"
      />

      {aiCorrectionDiff && (
        <SqlDiffDialog
          open={showDiffDialog}
          onClose={() => setShowDiffDialog(false)}
          originalSql={aiCorrectionDiff.original}
          correctedSql={aiCorrectionDiff.corrected}
        />
      )}
    </>
  );
}
